<template>
  <v-card
    data-testid="questionScoringCard-card-main"
    class="question-scoring-card px-5 py-4 mb-4"
    min-height="72"
    outlined
    :class="{ 'question-scoring-card--indented': isInsideSection }"
    :style="cardStyle"
    @click.native="emitActivation"
    @focusin.native="emitActivation"
  >
    <header class="d-flex align-center mb-4">
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-icon v-on="on" class="me-2" color="blue-grey">
            {{ questionTypeIcon }}
          </v-icon>
        </template>

        {{ questionTypeLabel }}
      </v-tooltip>

      <span
        v-snip="2"
        class="body-2 font-weight-600 blue-grey--text text--darken-2"
      >
        {{ item.name || fallbackQuestionLabel }}
      </span>
    </header>

    <div class="px-7 mb-2">
      <section v-if="isTextLikeQuestion" class="d-flex flex-column flex-nowrap">
        <p class="body-3 blue-grey--text text--darken-1 mb-6">
          {{ $t('routines.scoring.text_like_config_description') }}
        </p>

        <v-text-field
          v-disable-number-scroll
          class="question-scoring-card__line-input"
          data-testid="questionScoring-ifAnswered"
          dense
          hide-details="auto"
          max-width="300px"
          min="0"
          step="1"
          type="number"
          :rules="scoreFieldRules"
          :suffix="$t('routines.scoring.points_abbreviation')"
          :value="currentScoring.pointsIfAnswered"
          @input="updateScoringField('pointsIfAnswered', $event)"
        />
      </section>

      <section v-else-if="isSignatureQuestion">
        <p
          data-testid="questionScoringCard-text-notApplicable"
          class="body-3 blue-grey--text text--darken-1 mb-0"
        >
          {{ $t('routines.scoring.signature_not_applicable') }}
        </p>
      </section>

      <section v-else-if="isOptionQuestion">
        <p class="body-3 blue-grey--text text--darken-1 my-4">
          {{ $t('routines.scoring.select_like_config_description') }}
        </p>

        <div class="mt-8">
          <v-row
            v-for="(option, optionIndex) in itemOptions"
            class="d-flex align-center mb-2"
            :key="option.id || option._id || `option-${optionIndex}`"
          >
            <v-col
              cols="8"
              class="question-scoring-card__option-name body-2 blue-grey--text text--darken-3 mr-4"
            >
              {{
                option.name ||
                  $t('routines.scoring.option_label', {
                    number: optionIndex + 1,
                  })
              }}
            </v-col>

            <v-col cols="4">
              <v-text-field
                v-disable-number-scroll
                dense
                hide-details="auto"
                type="number"
                min="0"
                step="1"
                class="question-scoring-card__option-input"
                :data-testid="`questionScoring-option-${optionIndex}`"
                :rules="scoreFieldRules"
                :suffix="$t('routines.scoring.points_abbreviation')"
                :value="option.scoringPoints"
                @input="updateOptionScore(optionIndex, $event)"
              />
            </v-col>
          </v-row>
        </div>
      </section>

      <section v-else-if="isCheckboxQuestion">
        <p class="body-3 blue-grey--text text--darken-1 my-4">
          {{ $t('routines.scoring.checkboxes_config_description') }}
        </p>

        <div class="mt-8">
          <v-row
            v-for="(option, optionIndex) in itemOptions"
            class="mb-2"
            :key="option.id || option._id || `checkbox-option-${optionIndex}`"
          >
            <v-col cols="8">
              <div
                class="question-scoring-card__option-name body-3 blue-grey--text text--darken-3 mr-4"
              >
                {{
                  option.name ||
                    $t('routines.scoring.option_label', {
                      number: optionIndex + 1,
                    })
                }}
              </div>
            </v-col>

            <v-col cols="4">
              <v-checkbox
                dense
                hide-details
                class="question-scoring-card__checkbox-toggle caption mt-0 pt-0"
                :data-testid="`questionScoring-checkbox-${optionIndex}`"
                :input-value="Boolean(option.countsTowardScore)"
                :label="$t('common.required')"
                @change="updateCheckboxOptionSelection(optionIndex, $event)"
              />
            </v-col>
          </v-row>
        </div>

        <section class="question-scoring-card__score-column mt-8">
          <v-text-field
            v-disable-number-scroll
            dense
            hide-details="auto"
            type="number"
            min="0"
            step="1"
            class="question-scoring-card__line-input"
            data-testid="questionScoring-ifAllSelected"
            :rules="scoreFieldRules"
            :suffix="$t('routines.scoring.points_abbreviation')"
            :value="currentScoring.pointsIfAllSelected"
            @input="updateScoringField('pointsIfAllSelected', $event)"
          />
        </section>
      </section>

      <section v-else-if="isNumberQuestion">
        <p class="body-3 blue-grey--text text--darken-1 mt-4 mb-8">
          {{ $t('routines.scoring.number_config_description') }}
        </p>

        <p class="body-2 font-weight-500 blue-grey--text text--darken-2 mb-4">
          {{ $t('routines.scoring.setup_range') }}
        </p>

        <v-row class="d-flex flex-nowrap mt-4">
          <v-col cols="6" lg="6" sm="12">
            <v-text-field
              v-disable-number-scroll
              dense
              hide-details="auto"
              type="number"
              class="question-scoring-card__line-input"
              data-testid="questionScoring-rangeMin"
              :label="$t('routines.scoring.range_min')"
              :value="currentScoring.range && currentScoring.range.min"
              @input="updateRangeField('min', $event)"
            />
          </v-col>

          <v-col cols="6" lg="6" sm="12">
            <v-text-field
              v-disable-number-scroll
              dense
              hide-details="auto"
              type="number"
              class="question-scoring-card__line-input"
              data-testid="questionScoring-rangeMax"
              :value="currentScoring.range && currentScoring.range.max"
              :label="$t('routines.scoring.range_max')"
              @input="updateRangeField('max', $event)"
            />
          </v-col>
        </v-row>

        <p
          class="body-2 font-weight-500 blue-grey--text text--darken-2 mt-10 mb-4"
        >
          {{ $t('routines.scoring.setup_score') }}
        </p>

        <v-text-field
          v-disable-number-scroll
          dense
          hide-details="auto"
          type="number"
          min="0"
          step="1"
          class="question-scoring-card__line-input"
          data-testid="questionScoring-ifWithinRange"
          :rules="scoreFieldRules"
          :suffix="$t('routines.scoring.points_abbreviation')"
          :value="currentScoring.pointsIfWithinRange"
          @input="updateScoringField('pointsIfWithinRange', $event)"
        />
      </section>

      <section v-else-if="isScaleQuestion">
        <p class="body-3 blue-grey--text text--darken-1 mt-4">
          {{ $t('routines.scoring.scale_config_description') }}
        </p>

        <p class="body-3 blue-grey--text text--darken-1 mb-8">
          {{
            $t('routines.scoring.scale_range_summary', {
              min: resolvedScaleRange.min,
              max: resolvedScaleRange.max,
            })
          }}
        </p>

        <p class="body-2 font-weight-500 blue-grey--text text--darken-2 mb-4">
          {{ $t('routines.scoring.setup_range') }}
        </p>

        <v-row class="d-flex flex-nowrap mt-4">
          <v-col cols="6" lg="6" sm="12">
            <v-text-field
              v-disable-number-scroll
              class="question-scoring-card__line-input"
              data-testid="questionScoring-scaleRangeMin"
              dense
              hide-details="auto"
              type="number"
              :error="hasScaleGroupError"
              :label="$t('routines.scoring.range_min')"
              :value="currentScoring.range && currentScoring.range.min"
              @input="updateRangeField('min', $event)"
            />
          </v-col>

          <v-col cols="6" lg="6" sm="12">
            <v-text-field
              v-disable-number-scroll
              dense
              hide-details="auto"
              type="number"
              class="question-scoring-card__line-input"
              data-testid="questionScoring-scaleRangeMax"
              :error="hasScaleGroupError"
              :label="$t('routines.scoring.range_max')"
              :value="currentScoring.range && currentScoring.range.max"
              @input="updateRangeField('max', $event)"
            />
          </v-col>
        </v-row>

        <p
          class="body-2 font-weight-500 blue-grey--text text--darken-2 mt-10 mb-4"
        >
          {{ $t('routines.scoring.setup_score') }}
        </p>

        <v-text-field
          v-disable-number-scroll
          dense
          hide-details="auto"
          type="number"
          min="0"
          step="1"
          class="question-scoring-card__line-input"
          data-testid="questionScoring-ifWithinScaleRange"
          :error="hasScaleGroupError"
          :rules="scoreFieldRules"
          :suffix="$t('routines.scoring.points_abbreviation')"
          :value="currentScoring.pointsIfWithinRange"
          @input="updateScoringField('pointsIfWithinRange', $event)"
        />

        <p
          v-if="scaleGroupErrorMessage"
          data-testid="questionScoring-scaleGroupError"
          class="question-scoring-card__group-error caption error--text mt-2 mb-0"
        >
          {{ scaleGroupErrorMessage }}
        </p>
      </section>
    </div>
  </v-card>
</template>

<script>
import { ITEM_TYPES } from '@/components/Checklists/ChecklistCreation/constants'

import {
  isCheckboxQuestion,
  isNumberQuestion,
  isOptionQuestion,
  isSignatureQuestion,
  isScaleQuestion,
  isTextLikeQuestion,
  getScoringItemId,
  normalizeQuestionScoreInput
} from './scoring/scoringShared'
import { toNullableNumber } from './utils'
import {
  createQuestionRules,
  getScaleGroupErrorKey
} from './scoring/scoringValidation'

import { buildUpdatedOptionDraft } from './questionScoringDraft'

const QUESTION_TYPE_ICONS = {
  [ITEM_TYPES.IMAGE]: 'mdi-image-outline',
  [ITEM_TYPES.DATE]: 'mdi-calendar',
  [ITEM_TYPES.NUMBER]: 'mdi-pound',
  [ITEM_TYPES.SCALE]: 'linear_scale',
  [ITEM_TYPES.CHECKBOXES]: 'mdi-checkbox-marked-outline',
  [ITEM_TYPES.RADIOS]: 'mdi-radiobox-marked',
  [ITEM_TYPES.SELECT]: 'mdi-arrow-down-drop-circle-outline',
  [ITEM_TYPES.TEXT]: 'mdi-text-short',
  [ITEM_TYPES.ACCOUNT]: 'mdi-account-outline',
  [ITEM_TYPES.SIGNATURE]: 'mdi-signature-freehand',
}

export default {
  name: 'QuestionScoringCard',

  data() {
    return {
      localOptionDraft: [],
    }
  },

  props: {
    item: {
      type: Object,
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
    index: {
      type: Number,
      required: true,
    },
    isInsideSection: {
      type: Boolean,
      default: false,
    },
    borderColor: {
      type: String,
      default: null,
    },
  },

  computed: {
    fallbackQuestionLabel() {
      return this.$t('routines.scoring.question_label', {
        number: this.index + 1,
      })
    },

    currentScoring() {
      return this.item?.scoring || {}
    },

    itemOptions() {
      if (this.localOptionDraft.length) {
        return this.localOptionDraft
      }

      return Array.isArray(this.item?.options) ? this.item.options : []
    },

    isTextLikeQuestion() {
      return isTextLikeQuestion(this.item)
    },

    isOptionQuestion() {
      return isOptionQuestion(this.item)
    },

    isCheckboxQuestion() {
      return isCheckboxQuestion(this.item)
    },

    isNumberQuestion() {
      return isNumberQuestion(this.item)
    },

    isScaleQuestion() {
      return isScaleQuestion(this.item)
    },

    isSignatureQuestion() {
      return isSignatureQuestion(this.item)
    },

    questionTypeLabel() {
      return this.$t(`checklist.types.${this.item?.type}`)
    },

    questionTypeIcon() {
      return (
        QUESTION_TYPE_ICONS[this.item?.type] ||
        QUESTION_TYPE_ICONS[ITEM_TYPES.TEXT]
      )
    },

    resolvedItemId() {
      return getScoringItemId(this.item, this.index)
    },

    scoreFieldRules() {
      return createQuestionRules({
        item: this.item,
        translate: key => this.$t(`routines.scoring.${key}`),
      })
    },

    scaleGroupErrorKey() {
      return getScaleGroupErrorKey(this.item)
    },

    hasScaleGroupError() {
      return Boolean(this.scaleGroupErrorKey)
    },

    scaleGroupErrorMessage() {
      return this.scaleGroupErrorKey
        ? this.$t(`routines.scoring.${this.scaleGroupErrorKey}`)
        : ''
    },

    resolvedScaleRange() {
      return {
        min: toNullableNumber(this.item?.min) ?? 0,
        max: toNullableNumber(this.item?.max) ?? 0,
      }
    },

    sectionBorderTopWidthStyle() {
      if (!this.borderColor) {
        return ''
      }

      return 'border-top-width: 4px !important; '
    },

    cardStyle() {
      if (this.active) {
        return `border-color: #167DEC !important; ${this.sectionBorderTopWidthStyle}box-shadow: inset 0 0 0 1px #167DEC !important;`
      }

      if (!this.borderColor) {
        return ''
      }

      return `border-top-width: 4px !important; border-top-color: ${this.borderColor} !important;`
    },
  },

  methods: {
    emitActivation() {
      this.$emit('activateQuestion', this.resolvedItemId)
    },

    emitItemPatch(scoring) {
      this.$emit('change', {
        itemId: this.resolvedItemId,
        scoring,
      })
    },

    emitOptionsPatch(options) {
      this.$emit('change', {
        itemId: this.resolvedItemId,
        options,
      })
    },

    updateOptionField(optionIndex, field, valueTransformer) {
      const nextValue = valueTransformer(this.itemOptions[optionIndex])
      const options = buildUpdatedOptionDraft(
        this.itemOptions,
        optionIndex,
        field,
        nextValue
      )

      this.localOptionDraft = options

      this.emitOptionsPatch(options)
    },

    updateScoringField(field, value) {
      const scoring = {
        ...(this.item?.scoring || {}),
        enabled: true,
        [field]: normalizeQuestionScoreInput(value),
      }

      this.emitItemPatch(scoring)
    },

    updateOptionScore(optionIndex, value) {
      this.updateOptionField(optionIndex, 'scoringPoints', () =>
        normalizeQuestionScoreInput(value)
      )
    },

    updateCheckboxOptionSelection(optionIndex, value) {
      this.updateOptionField(optionIndex, 'countsTowardScore', () =>
        Boolean(value)
      )
    },

    updateRangeField(field, value) {
      const scoring = {
        ...this.item?.scoring,
        enabled: true,
        range: {
          ...this.item?.scoring?.range,
          [field]: toNullableNumber(value),
        },
      }

      this.emitItemPatch(scoring)
    },
  },

  watch: {
    'item.options': {
      deep: true,
      immediate: true,
      handler(options) {
        this.localOptionDraft = Array.isArray(options)
          ? options.map(option => ({ ...option }))
          : []
      },
    },
  },
}
</script>

<style scoped lang="scss">
.question-scoring-card {
  border-color: $color-blue-grey-lighten2 !important;
  border-top-width: 4px;
  border-top-style: solid;
}

.question-scoring-card--indented {
  margin-left: 28px;
  width: calc(100% - 28px);
}

.question-scoring-card__score-column {
  flex: 1;
  min-width: 0;
}

.question-scoring-card__line-input {
  max-width: 200px;
}

.question-scoring-card__option-name {
  flex: 1;
  min-width: 0;
}

.question-scoring-card__checkbox-toggle {
  flex: 0 0 auto;
}

.question-scoring-card__option-input {
  width: 160px;
}

.question-scoring-card__checkbox-toggle::v-deep .v-label {
  color: $color-blue-grey-darken3;
  font-size: $font-size-12 !important;
  line-height: 16px;
  letter-spacing: 0.4px !important;
}
</style>
